(() => {
  if (window.__dlCartFlowSafeV8) return;
  window.__dlCartFlowSafeV8 = true;

  const HATS = {
    white: { id: '48460550013113', label: 'White Richardson 112', x: 49.5, y: 42.5 },
    black: { id: '48460549980345', label: 'Black Richardson 112', x: 49.5, y: 45.8 },
    canvas: { id: '49115317928121', label: 'Canvas Snapback', x: 49.95, y: 45.5 }
  };
  const byId = Object.fromEntries(Object.entries(HATS).map(([key, value]) => [value.id, key]));

  function tell(root, message, target) {
    const notice = root.querySelector('[data-notice]');
    const results = root.querySelector('[data-results]');
    if (notice) {
      notice.textContent = message;
      notice.hidden = false;
    }
    if (results) results.hidden = false;
    if (target) setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 30);
  }

  function prerequisiteNotice(root, consent) {
    root.querySelectorAll('[data-notice],[data-bundle-warning]').forEach(el => { el.hidden = true; });
    root.querySelectorAll('[data-dl-prerequisite-notice]').forEach(el => el.remove());
    const area = consent ? root.querySelector('[data-rights]')?.closest('.rights') : root.querySelector('.hatpicker');
    if (!area) return;
    const box = document.createElement('div');
    box.dataset.dlPrerequisiteNotice = consent ? 'consent' : 'drops';
    box.setAttribute('role', 'alert');
    box.style.cssText = 'display:block;width:100%;box-sizing:border-box;background:#fff3d5;color:#111;border-radius:16px;padding:16px;margin:12px 0;font-size:16px;line-height:1.45';
    box.textContent = consent ? 'Please confirm you have permission to use your uploaded image(s). ☝️' : 'Please create and save both Drop 1 and Drop 2 below before adding your bundle to cart. 👇';
    if (!consent) {
      box.style.order = '2';
      box.style.gridColumn = '1 / -1';
    }
    area.insertAdjacentElement(consent ? 'afterend' : 'beforebegin', box);
    const target = consent ? area : (root.querySelector('.stageviewport') || root.querySelector('.stagewrap') || area);
    requestAnimationFrame(() => window.scrollTo({top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - 20), behavior:'instant'}));
  }

  function currentKey(root) {
    return HATS[root.dataset.hat] ? root.dataset.hat : 'white';
  }

  function syncHatChecks(root, key) {
    root.querySelectorAll('[data-hat-choice]').forEach(button => {
      const selected = button.dataset.hatChoice === key;
      button.classList.toggle('active', selected);
      button.classList.toggle('dl-guide-confirmed', selected);
    });
  }

  function applyHat(root, key, { updateSelect = true } = {}) {
    const meta = HATS[key];
    if (!meta) return false;
    const button = root.querySelector(`[data-hat-choice="${key}"]`);
    const hat = root.querySelector('[data-hat-preview]');
    const select = root.querySelector('[data-variants]');
    root.dataset.hat = key;
    syncHatChecks(root, key);
    if (hat && button?.dataset.hatSrc) {
      hat.removeAttribute('srcset');
      hat.removeAttribute('sizes');
      hat.src = button.dataset.hatSrc;
      hat.alt = button.querySelector('img')?.alt || meta.label;
    }
    if (updateSelect && select && select.value !== meta.id && [...select.options].some(option => option.value === meta.id)) {
      select.value = meta.id;
    }
    root.dispatchEvent(new CustomEvent('droplit:hat-changed', {
      bubbles: true,
      detail: { key, id: meta.id, label: meta.label, src: button?.dataset.hatSrc || '' }
    }));
    return true;
  }

  function cleanDropName(drop, number) {
    const fallback = `Drop ${number}`;
    let name = String(drop?.title || drop?.originalTitle || fallback).trim();
    if (/^https?:\/\//i.test(name)) {
      try {
        name = decodeURIComponent(new URL(name).pathname.split('/').pop() || fallback);
      } catch (_) {
        name = fallback;
      }
    }
    name = name.replace(/\.(png|jpe?g|webp|heic|heif)$/i, '');
    name = name.replace(/-(optimized|cropped)$/i, '');
    name = name.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    return name || fallback;
  }

  function artworkValue(drop) {
    return drop?.production || drop?.preview || drop?.originalPreview || '';
  }

  function canvasBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('Placement proof image could not be created.'));
      }, 'image/png');
    });
  }

  function loadImage(source) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      let ownedUrl = '';
      if (source instanceof Blob) {
        ownedUrl = URL.createObjectURL(source);
        image.src = ownedUrl;
      } else {
        image.crossOrigin = 'anonymous';
        image.src = String(source || '');
      }
      image.onload = () => resolve({ image, ownedUrl });
      image.onerror = () => {
        if (ownedUrl) URL.revokeObjectURL(ownedUrl);
        reject(new Error('A Drop image could not be loaded for its placement proof.'));
      };
    });
  }

  function drawContained(context, image, x, y, width, height) {
    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;
    const ratio = Math.min(width / naturalWidth, height / naturalHeight);
    const drawWidth = naturalWidth * ratio;
    const drawHeight = naturalHeight * ratio;
    context.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
  }

  async function makePlacementProof(root, drop, number) {
    const key = currentKey(root);
    const meta = HATS[key];
    const hatButton = root.querySelector(`[data-hat-choice="${key}"]`);
    const hatSource = hatButton?.dataset.hatSrc || root.querySelector('[data-hat-preview]')?.src;
    const artworkSource = drop?.cartArtworkFile instanceof Blob
      ? drop.cartArtworkFile
      : drop?.printFile instanceof Blob
      ? drop.printFile
      : drop?.file instanceof Blob
        ? drop.file
        : artworkValue(drop);
    if (!hatSource || !artworkSource) throw new Error(`Drop ${number} is missing artwork for its placement proof.`);

    const [{ image: hatImage, ownedUrl: hatUrl }, { image: artworkImage, ownedUrl: artworkUrl }] = await Promise.all([
      loadImage(hatSource),
      loadImage(artworkSource)
    ]);

    try {
      const width = 600;
      const header = 48;
      const stageHeight = 450;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = header + stageHeight;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('Placement proof canvas is unavailable.');

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, canvas.height);
      context.fillStyle = '#111111';
      context.font = '700 22px -apple-system, BlinkMacSystemFont, Arial, sans-serif';
      context.fillText(`DROP ${number} — SAVED`, 28, 40);
      context.textAlign = 'right';
      context.font = '500 18px -apple-system, BlinkMacSystemFont, Arial, sans-serif';
      context.fillStyle = '#555555';
      context.fillText(meta.label.toUpperCase(), width - 28, 40);
      context.textAlign = 'left';

      const gradient = context.createRadialGradient(width / 2, header + stageHeight * 0.43, 10, width / 2, header + stageHeight * 0.5, width * 0.62);
      gradient.addColorStop(0, '#ffffff');
      gradient.addColorStop(0.45, '#f4f3f0');
      gradient.addColorStop(1, '#deddd9');
      context.fillStyle = gradient;
      context.fillRect(0, header, width, stageHeight);
      drawContained(context, hatImage, 0, header, width, stageHeight);

      const scale = Math.max(0.4, Math.min(2.5, Number(drop?.scale) || 1));
      const boxSize = width * 0.18 * scale;
      const centerX = width * ((meta.x + (Number(drop?.positionX) || 0)) / 100);
      const centerY = header + stageHeight * ((meta.y + (Number(drop?.positionY) || 0)) / 100);
      const radius = Math.max(0, Math.min(50, Number(drop?.cornerRadius) || 0)) / 100 * boxSize;

      context.save();
      if (radius > 0) {
        context.beginPath();
        context.roundRect(centerX - boxSize / 2, centerY - boxSize / 2, boxSize, boxSize, radius);
        context.clip();
      }
      drawContained(context, artworkImage, centerX - boxSize / 2, centerY - boxSize / 2, boxSize, boxSize);
      context.restore();

      const blob = await canvasBlob(canvas);
      return new File([blob], `droplit-drop-${number}-placement-proof.png`, { type: 'image/png', lastModified: Date.now() });
    } finally {
      if (hatUrl) URL.revokeObjectURL(hatUrl);
      if (artworkUrl) URL.revokeObjectURL(artworkUrl);
    }
  }

  async function prepareArtwork(drop, number) {
    const source = drop?.printFile instanceof Blob ? drop.printFile : drop?.file instanceof Blob ? drop.file : null;
    if (!source) return;
    if (drop.cartArtworkSource === source && drop.cartArtworkFile) return;
    const { image, ownedUrl } = await loadImage(source);
    const canvas = document.createElement('canvas');
    try {
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      for (const edge of [1600, 1200, 1064]) {
        const ratio = Math.min(1, edge / Math.max(width, height));
        canvas.width = Math.max(1, Math.round(width * ratio));
        canvas.height = Math.max(1, Math.round(height * ratio));
        const context = canvas.getContext('2d', { alpha: true });
        if (!context) throw new Error('Unable to prepare your artwork. Your designs are still here.');
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const blob = await canvasBlob(canvas);
        if (blob.size <= 1750000) {
          drop.cartArtworkFile = new File([blob], `droplit-drop-${number}-print.png`, { type: 'image/png' });
          drop.cartArtworkSource = source;
          drop.cartPrintPixels = `${canvas.width} x ${canvas.height}`;
          return;
        }
      }
      throw new Error(`Drop ${number} is too detailed to upload safely at print resolution. Your designs are still here. Please contact us for help with this image.`);
    } finally {
      canvas.width = canvas.height = 1;
      image.src = '';
      if (ownedUrl) URL.revokeObjectURL(ownedUrl);
    }
  }

  const preparation = new WeakMap();

  function exportKey(root, drop) {
    return [drop.printFile || null, drop.file || null, artworkValue(drop), currentKey(root),
      root.querySelector(`[data-hat-choice="${currentKey(root)}"]`)?.dataset.hatSrc || root.querySelector('[data-hat-preview]')?.src,
      drop.scale, drop.positionX, drop.positionY, drop.cornerRadius];
  }

  function sameKey(a, b) {
    return a && b && a.length === b.length && a.every((value, i) => value === b[i]);
  }

  function ensureExports(root, indexes) {
    let state = preparation.get(root);
    if (!state) {
      state = { tail: Promise.resolve(), cache: new Map() };
      preparation.set(root, state);
    }
    const task = state.tail.catch(() => {}).then(async () => {
      for (const index of indexes) {
        for (let attempt = 0; attempt < 3; attempt++) {
          const drop = root.__dlSlots?.[index];
          if (!drop) { state.cache.delete(index); break; }
          const key = exportKey(root, drop);
          const cached = state.cache.get(index);
          if (sameKey(cached?.key, key)) {
            Object.assign(drop, cached.files);
            break;
          }
          const snapshot = { ...drop };
          if (!(snapshot.printFile instanceof Blob) && !(snapshot.file instanceof Blob)) {
            snapshot.cartArtworkFile = null;
            snapshot.cartArtworkSource = null;
            snapshot.cartPrintPixels = null;
          }
          await prepareArtwork(snapshot, index + 1);
          snapshot.proofHat = HATS[currentKey(root)].label;
          try {
            snapshot.proofFile = await makePlacementProof(root, snapshot, index + 1);
          } catch (error) {
            snapshot.proofFile = null;
            console.info('[DropLit placement proof]', error);
          }
          const current = root.__dlSlots?.[index];
          if (!current || !sameKey(key, exportKey(root, current))) {
            if (attempt === 2) throw new Error('Your design changed while preparing. Please save it and try again.');
            continue;
          }
          const files = Object.fromEntries(['cartArtworkFile', 'cartArtworkSource', 'cartPrintPixels', 'proofHat', 'proofFile'].map(name => [name, snapshot[name]]));
          Object.assign(current, files);
          if (snapshot.proofFile) state.cache.set(index, { key, files });
          else state.cache.delete(index);
          break;
        }
      }
    });
    state.tail = task;
    return task;
  }

  function prepareSaved(root) {
    setTimeout(() => {
      const indexes = [0, 1, 2, 3].filter(i => root.__dlSlots?.[i] && root.dataset[`dlSaved${i + 1}`] === '1');
      ensureExports(root, indexes).catch(error => console.info('[DropLit background preparation]', error));
    }, 0);
  }

  function addDrop(formData, number, drop) {
    const artworkKey = `properties[_Drop ${number} Artwork]`;
    const artwork = drop?.cartArtworkFile instanceof Blob ? drop.cartArtworkFile : drop?.printFile instanceof Blob ? drop.printFile : drop?.file instanceof Blob ? drop.file : null;
    if (artwork) formData.append(artworkKey, artwork, artwork.name || `droplit-drop-${number}-print.png`);
    else {
      const url = artworkValue(drop);
      if (url) formData.append(artworkKey, url);
    }
    if (drop?.proofFile instanceof Blob) {
      formData.append(`properties[_Drop ${number} Placement Proof]`, drop.proofFile, drop.proofFile.name || `droplit-drop-${number}-placement-proof.png`);
    }
    formData.append(`properties[_Drop ${number} Name]`, cleanDropName(drop, number));
    formData.append(`properties[_Drop ${number} Source]`, drop?.source || 'custom');
    formData.append(`properties[_Drop ${number} Scale]`, `${Math.round((Number(drop?.scale) || 1) * 100)}%`);
    formData.append(`properties[_Drop ${number} Left Right]`, String(Number(drop?.positionX) || 0));
    formData.append(`properties[_Drop ${number} Up Down]`, String(Number(drop?.positionY) || 0));
    if (drop?.proofHat) formData.append(`properties[_Drop ${number} Hat]`, String(drop.proofHat));
    if (drop?.cartPrintPixels || drop?.printPixelSize) formData.append(`properties[_Drop ${number} Print Pixels]`, String(drop.cartPrintPixels || drop.printPixelSize));
    if (drop?.backgroundRemoved || drop?.bgRemoved) formData.append(`properties[_Drop ${number} Background Removed]`, 'Yes');
    if (drop?.cropped) formData.append(`properties[_Drop ${number} Cropped]`, 'Yes');
    if (Number(drop?.cornerRadius) > 0) formData.append(`properties[_Drop ${number} Corner Radius]`, `${drop.cornerRadius}%`);
  }

  function clearPermissionHighlight(root) {
    const area = root.querySelector('[data-permission-area]') || root.querySelector('[data-rights]')?.closest('.rights');
    area?.classList.remove('dl-permission-attention');
  }

  function highlightPermission(root, rights) {
    const area = rights?.closest('.rights') || rights;
    if (!area) return;
    area.setAttribute('data-permission-area', '');
    area.classList.add('dl-permission-attention');
    setTimeout(() => area.scrollIntoView({ behavior: 'smooth', block: 'center' }), 30);
  }

  async function add(root) {
    const slots = root.__dlSlots || [];
    const button = root.querySelector('[data-add]');
    const rights = root.querySelector('[data-rights]');
    if (!slots[0] || !slots[1] || root.dataset.dlSaved1 !== '1' || root.dataset.dlSaved2 !== '1') {
      window.dispatchEvent(new CustomEvent('droplit:cart-result', { detail: { blocked: true, reason: 'drops_incomplete' } }));
      return prerequisiteNotice(root, false);
    }
    if (!rights?.checked) {
      window.dispatchEvent(new CustomEvent('droplit:cart-result', { detail: { blocked: true, reason: 'permission_missing' } }));
      return prerequisiteNotice(root, true);
    }
    clearPermissionHighlight(root);
    const meta = HATS[currentKey(root)];
    if (!meta) return tell(root, 'Please choose a hat before adding your bundle.');
    if (root.dataset.cartBusy === '1') return;
    if (slots.some((drop, index) => drop && root.dataset[`dlSaved${index + 1}`] !== '1')) return tell(root, 'Please save each Drop before adding your bundle.');

    root.dataset.cartBusy = '1';
    const oldHtml = button?.innerHTML;
    if (button) {
      button.dataset.busy = '1';
      button.disabled = true;
      button.innerHTML = '<span>PREPARING PRINT FILES…</span>';
    }

    try {
      await root.__dlUploadQueue;
      const selected = [0, 1, 2, 3].filter(index => slots[index]);
      await ensureExports(root, selected);
      const formData = new FormData();
      const bundleId = crypto.randomUUID();
      const itemForm = index => ({
        append(key, value, filename) {
          const match = key.match(/^properties\[(.*)\]$/);
          const target = match ? `items[${index}][properties][${match[1]}]` : `items[${index}][${key}]`;
          if (filename) formData.append(target, value, filename);
          else formData.append(target, value);
        }
      });
      const parent = itemForm(0);
      parent.append('id', meta.id);
      parent.append('quantity', '1');
      parent.append('properties[Hat Style]', meta.label);
      parent.append('properties[_DropLit Bundle ID]', bundleId);
      addDrop(parent, 1, slots[0]);
      addDrop(parent, 2, slots[1]);
      selected.filter(index => index >= 2).forEach((index, childIndex) => {
        const child = itemForm(childIndex + 1);
        child.append('id', '49208683135161');
        child.append('quantity', '1');
        child.append('parent_id', meta.id);
        child.append('properties[_DropLit Bundle ID]', bundleId);
        child.append('properties[_DropLit Extra]', String(index + 1));
        child.append('properties[Design]', `Drop ${index + 1} · ${cleanDropName(slots[index], index + 1)}`);
        addDrop(child, index + 1, slots[index]);
      });
      const payloadBytes = await new Response(formData).blob().then(blob => blob.size);
      if (payloadBytes > 8000000) throw new Error('This bundle is too large to upload safely. Your designs are still here. Please contact us for help.');
      console.info('[DropLit cart upload]', { bytes: payloadBytes, files: [...formData.values()].filter(value => value instanceof Blob).map(value => ({ bytes: value.size, type: value.type })) });
      if (button) button.innerHTML = '<span>ADDING YOUR BUNDLE…</span>';
      const response = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { Accept: 'application/json' } });
      if (!response.ok) {
        let message = response.status === 413 ? 'Shopify rejected the upload size. Your designs are still here. Please contact us for help. (413)' : `Could not add your bundle to cart. Your designs are still here. Please try again. (${response.status})`;
        try {
          const payload = await response.json();
          if (payload?.description) message = payload.description;
        } catch (_) {}
        throw new Error(message);
      }
      window.dispatchEvent(new CustomEvent('droplit:cart-result', { detail: { ok: true } }));
      window.location.assign('/cart');
    } catch (error) {
      root.dataset.cartBusy = '0';
      console.error('[DropLit cart flow]', error);
      window.dispatchEvent(new CustomEvent('droplit:cart-result', { detail: { ok: false, reason: 'cart_request_failed' } }));
      tell(root, error?.message || 'Could not prepare your print files. Please tap Save Drop again and retry.');
      if (button) {
        button.dataset.busy = '0';
        button.disabled = false;
        button.innerHTML = oldHtml;
      }
    }
  }

  function wire(root) {
    if (root.dataset.dlCartFlowSafeV8) return;
    root.dataset.dlCartFlowSafeV8 = '1';
    root.__dlEnsureExports = indexes => ensureExports(root, indexes);
    const clearPrerequisites = () => root.querySelectorAll('[data-dl-prerequisite-notice]').forEach(el => el.remove());
    root.addEventListener('change', event => { if (event.target.matches('[data-rights]')) clearPrerequisites(); });
    root.addEventListener('droplit:hat-changed', () => prepareSaved(root));
    root.addEventListener('droplit:drop-saved', () => {
      prepareSaved(root);
      if (root.dataset.dlSaved1 === '1' && root.dataset.dlSaved2 === '1') clearPrerequisites();
    });
    const select = root.querySelector('[data-variants]');
    const rights = root.querySelector('[data-rights]');
    root.querySelectorAll('[data-hat-choice]').forEach(button => button.addEventListener('click', () => applyHat(root, button.dataset.hatChoice)));
    if (select) {
      select.removeAttribute('aria-hidden');
      select.removeAttribute('tabindex');
      select.disabled = false;
      select.addEventListener('change', () => {
        const key = byId[String(select.value)];
        if (key) applyHat(root, key, { updateSelect: false });
      });
    }
    if (rights) {
      const area = rights.closest('.rights') || rights;
      area.setAttribute('data-permission-area', '');
      rights.addEventListener('change', () => {
        if (rights.checked) clearPermissionHighlight(root);
      });
    }
    root.addEventListener('click', event => {
      const button = event.target.closest?.('[data-add],[data-add-bottom]');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      add(root);
    }, true);
    const initial = select ? byId[String(select.value)] : null;
    applyHat(root, initial || currentKey(root));
  }

  function boot() {
    document.querySelectorAll('.dl').forEach(wire);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
